/**
 * PayrollView.js — Owner/manager payroll dashboard
 *
 * Tabs: Overview · Employees · Tips · History
 *
 * Overview:   Hero row · Alert list · Dept bar · Labor intelligence · Status strip · Top earners
 * Employees:  Ranked cost list · Per-employee drilldown sheet
 * Tips:       Collection summary · Per-employee distribution · Pool formula
 * History:    All periods list with status — tap to load
 *
 * Data sources (all live via onSnapshot):
 *   payPeriods/{id}           — bounds, status, revenueForPeriod, reviewedAt
 *   payroll_ledger            — typed entries for selected period
 *   employees                 — name, position, role, payStructure
 *   Timesheet (clockOut null) — open shifts blocking finalization
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  collection, query, where, orderBy,
  onSnapshot, getDocs, doc, updateDoc, limit, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { useBodyScrollLock } from '../../utilities/useBodyScrollLock';
import { PayrollTab } from './SettingsView';

const fncs             = getFunctions();
const lockFn           = httpsCallable(fncs, 'lockPayPeriod');
const periodExportFn   = httpsCallable(fncs, 'exportPayrollCSV');
const ytdExportFn      = httpsCallable(fncs, 'exportYTDSummaryCSV');
const manualClockOutFn = httpsCallable(fncs, 'manualClockOut');

// ── Formatting ────────────────────────────────────────────────────────────────

const fmt$ = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtHrs = (n) => `${Number(n || 0).toFixed(1)}h`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

function getPeriodDates(id) {
  const parts = (id || '').split('_to_');
  if (parts.length < 2) return null;
  return { startStr: parts[0], endStr: parts[1] };
}

function getWeekStartsInRange(startStr, endStr) {
  const starts = [];
  let cur = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr   + 'T00:00:00');
  while (cur < end) {
    starts.push(cur.toISOString().slice(0, 10));
    cur = new Date(cur.getTime() + 7 * 24 * 60 * 60 * 1000);
  }
  return starts;
}

function parsePeriod(id) {
  const parts = (id || '').split('_to_');
  if (parts.length < 2) return { label: id || '', short: id || '' };
  const s  = new Date(parts[0] + 'T00:00:00');
  const e  = new Date(parts[1] + 'T00:00:00');
  const mo = { month: 'short', day: 'numeric' };
  return {
    label: `${s.toLocaleDateString('en-US', mo)} – ${e.toLocaleDateString('en-US', { ...mo, year: 'numeric' })}`,
    short: `${s.toLocaleDateString('en-US', mo)} – ${e.toLocaleDateString('en-US', mo)}`,
  };
}

// ── Dept helpers ──────────────────────────────────────────────────────────────

const DEPT_COLORS = { FOH: '#3b82f6', BOH: '#f97316', Management: '#8b5cf6', Other: '#94a3b8' };

function empDept(emp) {
  if (!emp) return 'Other';
  const pos  = emp.position || '';
  const role = emp.role     || '';
  if (role === 'owner' || role === 'manager' || pos === 'Manager' || pos === 'Owner') return 'Management';
  if (['Cook', 'Dishwasher', 'Expo'].includes(pos)) return 'BOH';
  if (['Server', 'Host', 'Bartender'].includes(pos)) return 'FOH';
  return 'Other';
}

// ── Ledger aggregation ────────────────────────────────────────────────────────

function aggregateLedger(ledger, empMap) {
  const byId = {};

  ledger.forEach((e) => {
    if (!e.employeeId) return;
    const id = e.employeeId;
    if (!byId[id]) {
      const emp = empMap[id] || {};
      byId[id] = {
        id,
        name:     e.employeeName || emp.name || 'Unknown',
        position: emp.position   || '',
        role:     emp.role       || 'employee',
        isTipped: !!(emp.payStructure?.tipped?.isTippedEmployee),
        hours: 0, hourlyHours: 0, hourlyAmount: 0,
        baseAmount: 0, tipAmount: 0, commAmount: 0, bonusAmount: 0, otPremium: 0, otHours: 0,
      };
    }
    const r      = byId[id];
    const amount = Number(e.amount || 0);
    const hours  = Number(e.hours  || 0);

    if (e.type === 'base') {
      r.hours      += hours;
      r.baseAmount += amount;
      if (hours > 0 && amount > 0) { r.hourlyHours += hours; r.hourlyAmount += amount; }
    } else if (e.type === 'tip')           { r.tipAmount   += amount; }
    else if (e.type === 'commission')       { r.commAmount  += amount; }
    else if (e.type === 'overtime_premium') { r.otHours += hours; r.otPremium += amount; }
    else if (e.type === 'adjustment')       { r.bonusAmount += amount; }
  });

  return Object.values(byId).map((r) => ({
    ...r,
    grossTotal:    r.baseAmount + r.tipAmount + r.commAmount + r.bonusAmount + r.otPremium,
    effectiveRate: r.hourlyHours > 0 ? r.hourlyAmount / r.hourlyHours : null,
  })).sort((a, b) => b.grossTotal - a.grossTotal);
}

// ── Sub-tab config ────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',   label: 'Overview'  },
  { id: 'employees',  label: 'Employees' },
  { id: 'tips',       label: 'Tips'      },
  { id: 'history',    label: 'History'   },
  { id: 'settings',   label: 'Settings'  },
];

// ─────────────────────────────────────────────────────────────────────────────
// PayrollView
// ─────────────────────────────────────────────────────────────────────────────

const PayrollView = ({ user, restaurantId }) => {
  const [periods,        setPeriods]        = useState([]);
  const [selectedId,     setSelectedId]     = useState(null);
  const [period,         setPeriod]         = useState(null);
  const [ledger,         setLedger]         = useState([]);
  const [empMap,         setEmpMap]         = useState({});
  const [openShifts,     setOpenShifts]     = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [loadingLedger,  setLoadingLedger]  = useState(false);
  const [subTab,         setSubTab]         = useState('overview');
  const [drillEmp,       setDrillEmp]       = useState(null);
  const [editingRev,     setEditingRev]     = useState(false);
  const [revInput,       setRevInput]       = useState('');
  const [markingRev,     setMarkingRev]     = useState(false);
  const [approving,      setApproving]      = useState(false);
  const [approveErr,     setApproveErr]     = useState('');
  const [scheduledHours,    setScheduledHours]    = useState({}); // { empId: totalScheduledHrs }
  const [scheduleShiftsByEmp, setScheduleShiftsByEmp] = useState({}); // { empId: [{date, startTime: Date}] }
  const [periodTimesheets,  setPeriodTimesheets]  = useState([]); // closed timesheets within period
  const [todayClosedShifts, setTodayClosedShifts] = useState([]); // closed timesheets from today
  const [ytdYear,           setYtdYear]           = useState(() => new Date().getFullYear());
  const [ytdLoading,        setYtdLoading]        = useState(false);
  const [ytdError,          setYtdError]          = useState('');
  const [periodExporting,   setPeriodExporting]   = useState(false);
  const [periodExportErr,   setPeriodExportErr]   = useState('');

  // ── Employees ──────────────────────────────────────────────────────────────
  useEffect(() => {
    return onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'employees'),
      (snap) => {
        const m = {};
        snap.docs.forEach((d) => { m[d.id] = { id: d.id, ...d.data() }; });
        setEmpMap(m);
      }
    );
  }, [restaurantId]);

  // ── Pay periods list ───────────────────────────────────────────────────────
  useEffect(() => {
    return onSnapshot(
      query(
        collection(db, 'restaurants_data', restaurantId, 'payPeriods'),
        orderBy('startDate', 'desc'),
        limit(12),
      ),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPeriods(list);
        if (list.length > 0) setSelectedId((prev) => prev || list[0].id);
        setLoadingPeriods(false);
      },
      () => setLoadingPeriods(false),
    );
  }, [restaurantId]);

  // ── Selected period doc ────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedId) return;
    return onSnapshot(
      doc(db, 'restaurants_data', restaurantId, 'payPeriods', selectedId),
      (snap) => {
        if (!snap.exists()) return;
        const d = { id: snap.id, ...snap.data() };
        setPeriod(d);
        setRevInput(d.revenueForPeriod != null ? String(d.revenueForPeriod) : '');
      }
    );
  }, [restaurantId, selectedId]);

  // ── Ledger for selected period ─────────────────────────────────────────────
  useEffect(() => {
    if (!selectedId) return;
    setLoadingLedger(true);
    return onSnapshot(
      query(
        collection(db, 'restaurants_data', restaurantId, 'payroll_ledger'),
        where('payPeriodId', '==', selectedId),
      ),
      (snap) => {
        setLedger(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingLedger(false);
      },
      () => setLoadingLedger(false),
    );
  }, [restaurantId, selectedId]);

  // ── Open timesheets ────────────────────────────────────────────────────────
  useEffect(() => {
    return onSnapshot(
      query(
        collection(db, 'restaurants_data', restaurantId, 'Timesheet'),
        where('clockOutTime', '==', null),
      ),
      (snap) => setOpenShifts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {},
    );
  }, [restaurantId]);

  // ── Scheduled hours for pay period (to detect over-schedule) ──────────────
  useEffect(() => {
    if (!selectedId) return;
    const dates = getPeriodDates(selectedId);
    if (!dates) return;

    const weekStarts = getWeekStartsInRange(dates.startStr, dates.endStr);
    let cancelled = false;

    (async () => {
      const byEmp   = {};
      const byShift = {}; // { [empId]: [{date, startTime: Date}] }
      await Promise.all(weekStarts.map(async (weekStart) => {
        try {
          const snap = await getDocs(
            query(
              collection(db, 'restaurants_data', restaurantId, 'schedules', weekStart, 'shifts'),
              where('isOpenShift', '==', false),
            )
          );
          snap.docs.forEach((d) => {
            const s = d.data();
            if (!s.employeeId || !s.startTime || !s.endTime) return;
            const hrs = (s.endTime.toDate() - s.startTime.toDate()) / (1000 * 60 * 60);
            if (hrs > 0) byEmp[s.employeeId] = (byEmp[s.employeeId] || 0) + hrs;
            if (!byShift[s.employeeId]) byShift[s.employeeId] = [];
            byShift[s.employeeId].push({ date: s.date, startTime: s.startTime.toDate() });
          });
        } catch (_) { /* schedule may not exist for this week */ }
      }));
      if (!cancelled) {
        setScheduledHours(byEmp);
        setScheduleShiftsByEmp(byShift);
      }
    })();

    return () => { cancelled = true; };
  }, [restaurantId, selectedId]);

  // ── Closed timesheets for selected period (punctuality analysis) ───────────
  useEffect(() => {
    if (!selectedId) return;
    const dates = getPeriodDates(selectedId);
    if (!dates) return;
    const startTs = Timestamp.fromDate(new Date(dates.startStr + 'T00:00:00'));
    const endTs   = Timestamp.fromDate(new Date(dates.endStr   + 'T23:59:59'));
    return onSnapshot(
      query(
        collection(db, 'restaurants_data', restaurantId, 'Timesheet'),
        where('clockInTime', '>=', startTs),
        where('clockInTime', '<=', endTs),
        orderBy('clockInTime'),
      ),
      (snap) => {
        setPeriodTimesheets(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((t) => t.clockOutTime != null)
        );
      },
      () => {},
    );
  }, [restaurantId, selectedId]);

  // ── Today's closed timesheets (current clock-out status on employee rows) ──
  useEffect(() => {
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const endOfDay   = new Date(); endOfDay.setHours(23, 59, 59, 999);
    return onSnapshot(
      query(
        collection(db, 'restaurants_data', restaurantId, 'Timesheet'),
        where('clockInTime', '>=', Timestamp.fromDate(startOfDay)),
        where('clockInTime', '<=', Timestamp.fromDate(endOfDay)),
        orderBy('clockInTime'),
      ),
      (snap) => {
        setTodayClosedShifts(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((t) => t.clockOutTime != null)
        );
      },
      () => {},
    );
  }, [restaurantId]);

  // ── Core aggregates ────────────────────────────────────────────────────────
  const empRows    = useMemo(() => aggregateLedger(ledger, empMap), [ledger, empMap]);
  const totalLabor = useMemo(() => empRows.reduce((s, r) => s + r.grossTotal, 0), [empRows]);
  const totalHours = useMemo(() => empRows.reduce((s, r) => s + r.hours, 0),      [empRows]);
  const totalOT    = useMemo(() => empRows.reduce((s, r) => s + r.otPremium, 0),  [empRows]);
  const otEmpCount = useMemo(() => empRows.filter((r) => r.otHours > 0).length,   [empRows]);
  const approachOT = useMemo(() => empRows.filter((r) => r.hours >= 35 && r.otHours === 0), [empRows]);

  // ── Punctuality: late arrivals (clock-in >10 min after scheduled start) ────
  const lateArrivals = useMemo(() => {
    const incidents = [];
    periodTimesheets.forEach((t) => {
      const empId = t.serverID;
      if (!empId) return;
      const emp = empMap[empId];
      if (!emp) return;
      // Only flag hourly employees — salaried staff don't have shift-level accountability here
      if (emp.payStructure === 'salary') return;
      const clockIn = t.clockInTime?.toDate?.();
      if (!clockIn) return;
      const dateStr = clockIn.toISOString().slice(0, 10);
      const shifts  = scheduleShiftsByEmp[empId] || [];
      // Find the scheduled shift on the same day (closest start time to clockIn)
      const dayShifts = shifts.filter((s) => s.date === dateStr);
      if (dayShifts.length === 0) return;
      const match = dayShifts.reduce((best, s) =>
        Math.abs(clockIn - s.startTime) < Math.abs(clockIn - best.startTime) ? s : best
      );
      const lateMin = (clockIn - match.startTime) / (1000 * 60);
      if (lateMin >= 10) {
        incidents.push({
          empId,
          name: `${emp.firstName} ${emp.lastName}`,
          date: dateStr,
          lateMin: Math.round(lateMin),
          scheduledStart: match.startTime,
          actualClockIn: clockIn,
        });
      }
    });
    // Sort by date desc
    return incidents.sort((a, b) => b.date.localeCompare(a.date));
  }, [periodTimesheets, scheduleShiftsByEmp, empMap]);

  // { [empId]: number } — quick lookup of late-count per employee
  const latesByEmp = useMemo(() => {
    const m = {};
    lateArrivals.forEach((i) => { m[i.empId] = (m[i.empId] || 0) + 1; });
    return m;
  }, [lateArrivals]);

  const revenue  = period?.revenueForPeriod || 0;
  const laborPct = revenue > 0 ? (totalLabor / revenue) * 100 : null;

  // ── Tips aggregates ────────────────────────────────────────────────────────
  const totalTips = useMemo(() => empRows.reduce((s, r) => s + r.tipAmount, 0), [empRows]);

  // Employees who received tips OR are marked tipped with hours (for pool display)
  const tipRows = useMemo(() => {
    const withTips = empRows.filter((r) => r.tipAmount > 0 || (r.isTipped && r.hours > 0));
    return withTips.sort((a, b) => b.tipAmount - a.tipAmount);
  }, [empRows]);

  const tipEligibleHours = useMemo(
    () => tipRows.reduce((s, r) => s + r.hours, 0),
    [tipRows],
  );

  const tippedZero = useMemo(
    () => tipRows.filter((r) => r.isTipped && r.tipAmount === 0 && r.hours > 0),
    [tipRows],
  );

  // ── Dept breakdown ─────────────────────────────────────────────────────────
  const deptBreakdown = useMemo(() => {
    const m = {};
    empRows.forEach((r) => {
      const dept = empDept(empMap[r.id]);
      m[dept] = (m[dept] || 0) + r.grossTotal;
    });
    return Object.entries(m)
      .map(([name, amount]) => ({ name, amount, pct: totalLabor > 0 ? (amount / totalLabor) * 100 : 0 }))
      .sort((a, b) => b.amount - a.amount);
  }, [empRows, empMap, totalLabor]);

  // ── Labor intelligence ─────────────────────────────────────────────────────
  const laborIntel = useMemo(() => {
    const insights = [];

    // Cost per hour
    if (totalHours > 0) {
      const cph = totalLabor / totalHours;
      insights.push({
        label: 'Avg Cost / Hour',
        value: fmt$(cph),
        sub: 'across all paid hours',
        level: 'neutral',
      });
    }

    // Single employee concentration
    if (empRows.length > 0 && totalLabor > 0) {
      const top    = empRows[0];
      const topPct = (top.grossTotal / totalLabor) * 100;
      if (topPct > 20) {
        insights.push({
          label: 'Labor Concentration',
          value: `${fmtPct(topPct)} by one person`,
          sub: `${top.name} is your highest cost center`,
          level: topPct > 30 ? 'red' : 'amber',
        });
      }
    }

    // OT cost exposure if approachingOT employees hit threshold
    if (approachOT.length > 0) {
      const exposure = approachOT.reduce((s, r) => {
        const rate     = r.effectiveRate || (r.hourlyAmount / Math.max(r.hourlyHours, 1));
        const otHrsLeft = 40 - r.hours;
        return s + (otHrsLeft > 0 ? otHrsLeft * rate * 0.5 : 0);
      }, 0);
      if (exposure > 0) {
        insights.push({
          label: 'OT Exposure',
          value: fmt$(exposure),
          sub: `potential premium if ${approachOT.length} employee${approachOT.length > 1 ? 's' : ''} hit threshold`,
          level: 'amber',
        });
      }
    }

    // Tips-to-base ratio for tipped staff
    const tippedBase = empRows
      .filter((r) => r.isTipped || r.tipAmount > 0)
      .reduce((s, r) => s + r.baseAmount, 0);
    if (tippedBase > 0 && totalTips > 0) {
      const ratio = totalTips / tippedBase;
      insights.push({
        label: 'Tips-to-Base Ratio',
        value: `${ratio.toFixed(2)}x`,
        sub: 'tips vs base pay for tipped staff',
        level: 'neutral',
      });
    }

    // Labor vs 28% target gap
    if (laborPct !== null) {
      const gap = laborPct - 28;
      if (Math.abs(gap) >= 1) {
        insights.push({
          label: gap > 0 ? 'Over Labor Target' : 'Under Labor Target',
          value: `${gap > 0 ? '+' : ''}${fmtPct(gap)}`,
          sub: `vs 28% industry benchmark · ${gap > 0 ? fmt$(totalLabor - revenue * 0.28) + ' over' : 'within target'}`,
          level: gap > 5 ? 'red' : gap > 0 ? 'amber' : 'green',
        });
      }
    }

    return insights;
  }, [empRows, totalLabor, totalHours, totalOT, approachOT, totalTips, laborPct, revenue]); // eslint-disable-line

  // ── Alerts ─────────────────────────────────────────────────────────────────
  const alerts = useMemo(() => {
    const list = [];

    if (openShifts.length > 0)
      list.push({
        level: 'red',
        text: `${openShifts.length} open clock-out${openShifts.length > 1 ? 's' : ''} — hours cannot be finalized`,
        actionId: 'employees', actionLabel: 'View Employees',
      });

    if (tippedZero.length > 0)
      list.push({
        level: 'red',
        text: `${tippedZero.length} tipped employee${tippedZero.length > 1 ? 's' : ''} showing $0 in tips`,
        actionId: 'tips', actionLabel: 'View Tips',
      });

    if (!revenue && totalLabor > 0)
      list.push({
        level: 'amber',
        text: 'Revenue not entered — labor % unavailable',
        actionId: 'revenue', actionLabel: 'Enter Revenue',
      });

    if (approachOT.length > 0)
      list.push({
        level: 'amber',
        text: `${approachOT.length} employee${approachOT.length > 1 ? 's' : ''} approaching overtime (35+ hrs)`,
        actionId: 'employees', actionLabel: 'View Employees',
      });

    if (laborPct !== null && laborPct > 33)
      list.push({
        level: 'amber',
        text: `Labor at ${fmtPct(laborPct)} — above 33% target`,
        actionId: 'employees', actionLabel: 'View Employees',
      });

    const overScheduled = empRows.filter((r) => {
      const sched = scheduledHours[r.id];
      return typeof sched === 'number' && r.hours > sched + 4;
    });
    if (overScheduled.length > 0)
      list.push({
        level: 'amber',
        text: `${overScheduled.length} employee${overScheduled.length > 1 ? 's' : ''} worked 4+ hrs over their scheduled time`,
        actionId: 'employees', actionLabel: 'View Employees',
      });

    if (lateArrivals.length > 0) {
      const uniqueEmps = [...new Set(lateArrivals.map((i) => i.empId))];
      list.push({
        level: 'amber',
        text: `${lateArrivals.length} late arrival${lateArrivals.length > 1 ? 's' : ''} this period across ${uniqueEmps.length} employee${uniqueEmps.length > 1 ? 's' : ''}`,
        actionId: 'employees', actionLabel: 'View Employees',
      });
    }

    // ── Payroll export readiness (only employees with ledger entries this period) ──
    const noPosition = empRows.filter((r) => !r.position);
    if (noPosition.length > 0)
      list.push({
        level: 'amber',
        text: `${noPosition.length} employee${noPosition.length > 1 ? 's' : ''} missing a position — CSV job title column will be blank`,
        actionId: 'employees', actionLabel: 'Fix in Employees',
      });

    const noEmail = empRows.filter((r) => !empMap[r.id]?.email);
    if (noEmail.length > 0)
      list.push({
        level: 'amber',
        text: `${noEmail.length} employee${noEmail.length > 1 ? 's' : ''} missing an email — harder to match records in payroll software`,
        actionId: 'employees', actionLabel: 'Fix in Employees',
      });

    return list;
  }, [openShifts, tippedZero, revenue, totalLabor, approachOT, laborPct, empRows, empMap, scheduledHours, lateArrivals]);

  const redCount   = alerts.filter((a) => a.level === 'red').length;
  const isLocked   = period?.status === 'locked' || period?.status === 'exported';
  const isReviewed = !!period?.reviewedAt;
  const isCurrent  = periods.length > 0 && selectedId === periods[0]?.id;

  const stages = [
    { label: 'Hours',    done: openShifts.length === 0 && totalHours > 0, warn: openShifts.length > 0 },
    { label: 'Revenue',  done: revenue > 0,                                warn: !revenue && totalLabor > 0 },
    { label: 'Reviewed', done: isReviewed,                                 warn: false },
    { label: 'Approved', done: isLocked,                                   warn: false },
  ];

  const laborColor = laborPct === null
    ? 'text-neutral-300'
    : laborPct > 33 ? 'text-red-600' : laborPct > 28 ? 'text-amber-600' : 'text-emerald-600';

  // ── Actions ────────────────────────────────────────────────────────────────
  const saveRevenue = async () => {
    const val = parseFloat(revInput);
    if (!isNaN(val) && val >= 0 && selectedId) {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'payPeriods', selectedId),
        { revenueForPeriod: val }
      ).catch(() => {});
    }
    setEditingRev(false);
  };

  const markReviewed = async () => {
    if (!selectedId) return;
    setMarkingRev(true);
    try {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'payPeriods', selectedId),
        { reviewedAt: serverTimestamp(), reviewedBy: user.accountId }
      );
    } finally {
      setMarkingRev(false);
    }
  };

  const approvePeriod = async () => {
    setApproveErr('');
    setApproving(true);
    try {
      await lockFn({ restaurantId, payPeriodId: selectedId });
    } catch (err) {
      setApproveErr(err.message || 'Failed to approve. Try again.');
    } finally {
      setApproving(false);
    }
  };

  // ── Early returns ──────────────────────────────────────────────────────────
  if (loadingPeriods) {
    return <p className="text-sm text-neutral-400 text-center py-12">Loading payroll data…</p>;
  }

  if (periods.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-neutral-200 rounded-xl text-center px-8">
        <p className="text-base font-semibold text-neutral-400">No pay periods yet</p>
        <p className="text-sm text-neutral-300 mt-1">Periods are created automatically when employees clock in.</p>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* ── Period selector ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={selectedId || ''}
          onChange={(e) => { setSelectedId(e.target.value); setSubTab('overview'); }}
          className="flex-1 min-w-0 text-sm font-semibold border border-neutral-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-neutral-300"
        >
          {periods.map((p) => {
            const { label } = parsePeriod(p.id);
            const suffix    = p.status === 'locked' ? ' · Locked' : p.status === 'exported' ? ' · Exported' : '';
            return <option key={p.id} value={p.id}>{label}{suffix}</option>;
          })}
        </select>
        {isCurrent && (
          <span className="text-xs font-semibold px-2.5 py-1 bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full shrink-0">
            Current
          </span>
        )}
        {isLocked && (
          <span className="text-xs font-semibold px-2.5 py-1 bg-neutral-200 text-neutral-500 rounded-full shrink-0">
            🔒 Locked
          </span>
        )}
      </div>

      {/* ── Sub-tabs ─────────────────────────────────────────────────────── */}
      <div className="flex overflow-x-auto border-b border-neutral-200 -mx-0 gap-0">
        {TABS.map((t) => {
          const badge = t.id === 'employees' && empRows.length > 0 ? empRows.length
            : t.id === 'tips' && totalTips > 0 ? null
            : null;
          return (
            <button
              key={t.id}
              onClick={() => setSubTab(t.id)}
              className={`shrink-0 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px whitespace-nowrap ${
                subTab === t.id
                  ? 'border-neutral-900 text-neutral-900'
                  : 'border-transparent text-neutral-400 hover:text-neutral-600'
              }`}
            >
              {t.label}{badge != null ? ` (${badge})` : ''}
            </button>
          );
        })}
      </div>

      {/* ═══════════════════════ OVERVIEW ════════════════════════════════ */}
      {subTab === 'overview' && (
        <div className="space-y-5">

          {/* Hero row */}
          <div className="grid grid-cols-2 gap-3">

            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1">Total Labor</p>
              <p className="text-2xl font-bold text-neutral-900 leading-none mb-1">{fmt$(totalLabor)}</p>
              {revenue > 0 && <p className="text-xs text-neutral-400">of {fmt$(revenue)} revenue</p>}
              <p className="text-xs text-neutral-400 mt-0.5">{fmtHrs(totalHours)} total hours</p>
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1">Labor %</p>
              <p className={`text-2xl font-bold leading-none mb-1 ${laborColor}`}>
                {laborPct !== null ? fmtPct(laborPct) : '—%'}
              </p>
              {laborPct !== null && (
                <p className={`text-xs ${laborPct > 33 ? 'text-red-500' : laborPct > 28 ? 'text-amber-500' : 'text-emerald-600'}`}>
                  {laborPct > 33 ? 'Above threshold' : laborPct > 28 ? 'Watch closely' : 'On target'}
                </p>
              )}
              {!isLocked && (
                editingRev ? (
                  <input
                    type="number"
                    value={revInput}
                    onChange={(e) => setRevInput(e.target.value)}
                    onBlur={saveRevenue}
                    onKeyDown={(e) => e.key === 'Enter' && saveRevenue()}
                    placeholder="Revenue $"
                    className="w-full mt-1.5 text-xs px-2 py-1 border border-neutral-300 rounded focus:outline-none"
                    autoFocus
                  />
                ) : (
                  <button onClick={() => setEditingRev(true)} className="text-xs text-blue-600 mt-1.5 hover:underline block">
                    {laborPct !== null ? 'Edit revenue' : '+ Enter revenue'}
                  </button>
                )
              )}
            </div>

            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1">Overtime</p>
              <p className={`text-2xl font-bold leading-none mb-1 ${totalOT > 0 ? 'text-amber-600' : 'text-neutral-300'}`}>
                {totalOT > 0 ? fmt$(totalOT) : '—'}
              </p>
              <p className="text-xs text-neutral-400">
                {otEmpCount > 0 ? `${otEmpCount} employee${otEmpCount > 1 ? 's' : ''} with OT` : 'No OT this period'}
              </p>
              {approachOT.length > 0 && (
                <p className="text-xs text-orange-600 font-medium mt-0.5">{approachOT.length} approaching</p>
              )}
            </div>

            <div className={`rounded-xl p-4 border ${
              redCount > 0 ? 'bg-red-50 border-red-200' :
              alerts.length > 0 ? 'bg-amber-50 border-amber-200' :
              'bg-white border-neutral-200'
            }`}>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1">Alerts</p>
              <p className={`text-2xl font-bold leading-none mb-1 ${
                redCount > 0 ? 'text-red-600' : alerts.length > 0 ? 'text-amber-600' : 'text-emerald-500'
              }`}>
                {alerts.length > 0 ? alerts.length : '✓'}
              </p>
              <p className={`text-xs font-medium ${
                redCount > 0 ? 'text-red-600' : alerts.length > 0 ? 'text-amber-600' : 'text-emerald-600'
              }`}>
                {redCount > 0 ? `${redCount} need attention` : alerts.length > 0 ? 'Review suggested' : 'All clear'}
              </p>
            </div>
          </div>

          {/* Alert list */}
          {alerts.length > 0 && (() => {
            const ACTION_MAP = {
              employees: () => setSubTab('employees'),
              tips:      () => setSubTab('tips'),
              revenue:   () => { setSubTab('overview'); setEditingRev(true); },
            };
            return (
              <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100 overflow-hidden">
                {alerts.map((a, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 px-4 py-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="shrink-0 mt-0.5">{a.level === 'red' ? '🔴' : '🟡'}</span>
                      <p className="text-sm text-neutral-700">{a.text}</p>
                    </div>
                    {a.actionId && ACTION_MAP[a.actionId] && (
                      <button
                        onClick={ACTION_MAP[a.actionId]}
                        className="shrink-0 text-xs font-semibold text-blue-600 hover:underline whitespace-nowrap"
                      >
                        {a.actionLabel} →
                      </button>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Dept breakdown bar */}
          {deptBreakdown.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-3">Labor by Department</p>
              <div className="flex rounded-full overflow-hidden h-3.5 mb-3 bg-neutral-100">
                {deptBreakdown.map(({ name, pct }) => (
                  <div key={name} style={{ width: `${pct}%`, backgroundColor: DEPT_COLORS[name] || DEPT_COLORS.Other }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {deptBreakdown.map(({ name, amount, pct }) => (
                  <div key={name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: DEPT_COLORS[name] || DEPT_COLORS.Other }} />
                    <span className="text-xs font-medium text-neutral-700">{name}</span>
                    <span className="text-xs text-neutral-400">{fmt$(amount)}</span>
                    <span className="text-xs text-neutral-300">·</span>
                    <span className="text-xs text-neutral-400">{fmtPct(pct)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Labor intelligence ────────────────────────────────────────── */}
          {laborIntel.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-100">
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Labor Intelligence</p>
              </div>
              <div className="divide-y divide-neutral-100">
                {laborIntel.map((item, i) => (
                  <div key={i} className="flex items-start justify-between gap-4 px-4 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0">
                          {item.level === 'red' ? '🔴' : item.level === 'amber' ? '🟡' : item.level === 'green' ? '🟢' : '📊'}
                        </span>
                        <p className="text-sm font-semibold text-neutral-700">{item.label}</p>
                      </div>
                      <p className="text-xs text-neutral-400 mt-0.5 pl-6">{item.sub}</p>
                    </div>
                    <p className={`text-sm font-bold shrink-0 ${
                      item.level === 'red' ? 'text-red-600' :
                      item.level === 'amber' ? 'text-amber-600' :
                      item.level === 'green' ? 'text-emerald-600' :
                      'text-neutral-800'
                    }`}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Period status strip */}
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-4">Period Status</p>

            <div className="flex items-start mb-5">
              {stages.map((stage, i) => (
                <React.Fragment key={stage.label}>
                  <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      stage.done ? 'bg-emerald-500 text-white' :
                      stage.warn ? 'bg-red-100 text-red-600 border-2 border-red-300' :
                      'bg-neutral-100 text-neutral-400'
                    }`}>
                      {stage.done ? '✓' : stage.warn ? '!' : i + 1}
                    </div>
                    <span className={`text-xs font-medium text-center leading-tight ${
                      stage.done ? 'text-emerald-600' : stage.warn ? 'text-red-600' : 'text-neutral-400'
                    }`}>
                      {stage.label}
                    </span>
                  </div>
                  {i < stages.length - 1 && (
                    <div className={`flex-1 h-0.5 mt-4 mx-0.5 ${stage.done ? 'bg-emerald-300' : 'bg-neutral-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {openShifts.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-3 space-y-2">
                <p className="text-xs font-semibold text-red-700">
                  {openShifts.length} employee{openShifts.length > 1 ? 's have' : ' has'} an unclosed shift — hours cannot be finalized until all timesheets are closed.
                </p>
                <div className="space-y-1">
                  {openShifts.map((s) => {
                    const emp = empRows.find(e => e.id === s.serverID);
                    const name = emp?.name || s.name || s.serverID;
                    const since = s.clockInTime?.toDate
                      ? s.clockInTime.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : null;
                    return (
                      <button
                        key={s.id}
                        onClick={() => {
                          if (emp) { setSubTab('employees'); setDrillEmp(emp); }
                        }}
                        className="w-full flex items-center justify-between bg-white border border-red-200 rounded-lg px-3 py-2 text-left hover:bg-red-50 active:bg-red-100 transition-colors"
                      >
                        <div>
                          <p className="text-xs font-semibold text-neutral-800">{name}</p>
                          {since && <p className="text-xs text-neutral-400">Clocked in since {since}</p>}
                        </div>
                        <span className="text-xs font-semibold text-red-600 shrink-0 ml-3">Clock Out →</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!isLocked ? (
              <div className="space-y-2">
                {!isReviewed ? (
                  <button
                    onClick={markReviewed}
                    disabled={markingRev || openShifts.length > 0}
                    className="w-full py-2.5 text-sm font-semibold border-2 border-neutral-300 text-neutral-700 rounded-lg hover:border-neutral-400 disabled:opacity-40 transition-colors"
                  >
                    {markingRev ? 'Marking…' : 'Mark Period as Reviewed'}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <span className="text-emerald-600 text-sm">✓</span>
                    <p className="text-xs font-medium text-emerald-700">Period marked as reviewed</p>
                  </div>
                )}
                {isReviewed && (
                  <>
                    {redCount > 0 && (
                      <p className="text-xs text-red-600 text-center">
                        Resolve {redCount} red alert{redCount > 1 ? 's' : ''} before approving
                      </p>
                    )}
                    {approveErr && <p className="text-xs text-red-600 text-center">{approveErr}</p>}
                    <button
                      onClick={approvePeriod}
                      disabled={approving || redCount > 0}
                      className="w-full py-2.5 text-sm font-bold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-40 transition-colors"
                    >
                      {approving ? 'Approving…' : 'Approve & Lock Period'}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span>🔒</span>
                  <p className="text-sm font-semibold text-neutral-700">Period locked</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-2 leading-relaxed">
                    Export this period's payroll CSV to submit to your payroll company (Gusto, ADP, Paychex, etc.), or hand off to your bookkeeper.
                  </p>
                  <button
                    onClick={async () => {
                      setPeriodExporting(true);
                      setPeriodExportErr('');
                      try {
                        const res = await periodExportFn({ restaurantId, payPeriodId: selectedId });
                        const a = document.createElement('a');
                        a.href = res.data.signedUrl;
                        a.download = `payroll_${selectedId}.csv`;
                        a.click();
                      } catch (err) {
                        setPeriodExportErr(err?.message || 'Export failed. Please try again.');
                      } finally {
                        setPeriodExporting(false);
                      }
                    }}
                    disabled={periodExporting}
                    className="w-full py-2.5 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {periodExporting ? 'Generating…' : 'Download Payroll CSV'}
                  </button>
                  {periodExportErr && (
                    <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mt-2">{periodExportErr}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Top earners preview */}
          {empRows.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between">
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Top Labor Cost</p>
                <button onClick={() => setSubTab('employees')} className="text-xs text-blue-600 font-medium hover:underline">
                  View all →
                </button>
              </div>
              {empRows.slice(0, 3).map((emp) => (
                <button
                  key={emp.id}
                  onClick={() => { setSubTab('employees'); setDrillEmp(emp); }}
                  className="w-full text-left px-4 py-3 border-b border-neutral-50 last:border-0 flex items-center justify-between gap-3 hover:bg-neutral-50 active:bg-neutral-100 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-800 truncate">{emp.name}</p>
                    <p className="text-xs text-neutral-400">{emp.position || emp.role} · {fmtHrs(emp.hours)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-neutral-900">{fmt$(emp.grossTotal)}</p>
                    {totalLabor > 0 && (
                      <p className="text-xs text-neutral-400">{fmtPct((emp.grossTotal / totalLabor) * 100)} of labor</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════ EMPLOYEES ══════════════════════════════ */}
      {subTab === 'employees' && (
        <div className="space-y-2.5">
          {loadingLedger ? (
            <p className="text-sm text-neutral-400 text-center py-10">Loading…</p>
          ) : empRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-neutral-200 rounded-xl">
              <p className="text-sm font-medium text-neutral-400">No payroll data for this period</p>
              <p className="text-xs text-neutral-300 mt-1">Data appears as employees clock in and out.</p>
            </div>
          ) : (
            empRows.map((emp) => {
              const share         = totalLabor > 0 ? (emp.grossTotal / totalLabor) * 100 : 0;
              const hasOT         = emp.otHours > 0;
              const nearOT        = emp.hours >= 35 && emp.otHours === 0;
              const isClockedIn   = openShifts.some(s => s.serverID === emp.id);
              const clockedOutToday = !isClockedIn && todayClosedShifts.some(s => s.serverID === emp.id);
              const lateCount     = latesByEmp[emp.id] || 0;
              return (
                <button
                  key={emp.id}
                  onClick={() => setDrillEmp(emp)}
                  className="w-full text-left bg-white border border-neutral-200 rounded-xl px-4 py-3.5 hover:border-neutral-300 active:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-neutral-800">{emp.name}</p>
                        {isClockedIn && (
                          <span className="flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                            Clocked In
                          </span>
                        )}
                        {clockedOutToday && (
                          <span className="text-xs font-semibold px-1.5 py-0.5 bg-neutral-100 text-neutral-500 rounded-full">
                            Clocked Out
                          </span>
                        )}
                        {lateCount > 0 && (
                          <span className="text-xs font-semibold px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full">
                            Late ×{lateCount}
                          </span>
                        )}
                        {hasOT  && <span className="text-xs font-semibold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">OT</span>}
                        {nearOT && <span className="text-xs font-semibold px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full">Near OT</span>}
                      </div>
                      <p className="text-xs text-neutral-400 mt-0.5">{emp.position || emp.role} · {fmtHrs(emp.hours)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-bold text-neutral-900">{fmt$(emp.grossTotal)}</p>
                      <p className="text-xs text-neutral-400">{fmtPct(share)} of labor</p>
                    </div>
                  </div>
                  <div className="mt-2.5 h-1 bg-neutral-100 rounded-full overflow-hidden">
                    <div className="h-full bg-neutral-700 rounded-full" style={{ width: `${Math.min(share * 2.5, 100)}%` }} />
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}

      {/* ═══════════════════════ TIPS & DISTRIBUTION ═════════════════════ */}
      {subTab === 'tips' && (
        <div className="space-y-4">
          {totalTips === 0 && tippedZero.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-neutral-200 rounded-xl">
              <p className="text-sm font-medium text-neutral-400">No tip data for this period</p>
              <p className="text-xs text-neutral-300 mt-1">Tip entries are posted via the POS when shifts close.</p>
            </div>
          ) : (
            <>
              {/* Collection summary */}
              <div className="bg-white border border-neutral-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-3">Collection Summary</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-neutral-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-neutral-400 mb-1">Total Tips</p>
                    <p className="text-xl font-bold text-neutral-900">{fmt$(totalTips)}</p>
                  </div>
                  <div className="bg-neutral-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-neutral-400 mb-1">Tip-Eligible Hrs</p>
                    <p className="text-xl font-bold text-neutral-900">{fmtHrs(tipEligibleHours)}</p>
                  </div>
                  {tipEligibleHours > 0 && (
                    <div className="bg-neutral-50 rounded-xl p-3 text-center col-span-2">
                      <p className="text-xs text-neutral-400 mb-1">Avg Tips / Hour</p>
                      <p className="text-xl font-bold text-neutral-900">{fmt$(totalTips / tipEligibleHours)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pool formula explanation */}
              {tipRows.length > 1 && tipEligibleHours > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                  <p className="text-xs font-semibold text-blue-800 mb-1">Pool Distribution Formula</p>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Each employee's share = their hours ÷ total tip-eligible hours × total tips collected.
                    Every row below is auditable against this formula.
                  </p>
                </div>
              )}

              {/* Per-employee distribution */}
              {tipRows.length > 0 && (
                <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-neutral-100">
                    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Distribution</p>
                  </div>

                  {/* Header row */}
                  <div className="grid grid-cols-4 gap-2 px-4 py-2 border-b border-neutral-100 bg-neutral-50">
                    {['Employee', 'Hours', 'Share', 'Tips'].map((h) => (
                      <p key={h} className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">{h}</p>
                    ))}
                  </div>

                  {tipRows.map((emp) => {
                    const poolShare = tipEligibleHours > 0 ? (emp.hours / tipEligibleHours) * 100 : 0;
                    const hasZero   = emp.isTipped && emp.tipAmount === 0 && emp.hours > 0;
                    return (
                      <div
                        key={emp.id}
                        className={`grid grid-cols-4 gap-2 px-4 py-3 border-b border-neutral-50 last:border-0 items-center ${hasZero ? 'bg-red-50' : ''}`}
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-neutral-800 truncate">{emp.name}</p>
                          <p className="text-xs text-neutral-400 truncate">{emp.position || emp.role}</p>
                        </div>
                        <p className="text-xs text-neutral-600">{fmtHrs(emp.hours)}</p>
                        <div>
                          <p className="text-xs font-medium text-neutral-700">{fmtPct(poolShare)}</p>
                          {/* Mini share bar */}
                          <div className="mt-1 h-1 bg-neutral-100 rounded-full overflow-hidden w-full">
                            <div className="h-full bg-blue-400 rounded-full" style={{ width: `${poolShare}%` }} />
                          </div>
                        </div>
                        <div>
                          {hasZero ? (
                            <p className="text-xs font-semibold text-red-600">$0.00 ⚠</p>
                          ) : (
                            <p className="text-xs font-semibold text-neutral-800">{fmt$(emp.tipAmount)}</p>
                          )}
                          {emp.hours > 0 && emp.tipAmount > 0 && (
                            <p className="text-xs text-neutral-400">{fmt$(emp.tipAmount / emp.hours)}/hr</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Zero-tip warnings */}
              {tippedZero.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-semibold text-red-700 mb-1">Missing tip data</p>
                  <p className="text-xs text-red-600">
                    {tippedZero.map((r) => r.name).join(', ')} {tippedZero.length > 1 ? 'are' : 'is'} marked as tipped
                    but show $0 for this period. Verify tips were entered before approving payroll.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════ HISTORY ════════════════════════════════ */}
      {subTab === 'history' && (
        <div className="space-y-4">

          {/* YTD Export card */}
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-neutral-800 mb-0.5">Year-to-Date Summary</p>
            <p className="text-xs text-neutral-400 mb-3 leading-relaxed">
              Aggregates all locked periods in a calendar year into one CSV — 28 columns per employee for W-2 prep and payroll reconciliation.
            </p>
            <div className="flex items-center gap-2">
              <select
                value={ytdYear}
                onChange={(e) => { setYtdYear(Number(e.target.value)); setYtdError(''); }}
                className="flex-1 px-3 py-2 text-sm border border-neutral-200 rounded-lg text-neutral-800 bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                disabled={ytdLoading}
              >
                {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button
                onClick={async () => {
                  setYtdLoading(true);
                  setYtdError('');
                  try {
                    const res = await ytdExportFn({ restaurantId, year: ytdYear });
                    const { signedUrl, periodsIncluded, employeeCount } = res.data;
                    const a = document.createElement('a');
                    a.href = signedUrl;
                    a.download = `ytd_payroll_${ytdYear}.csv`;
                    a.click();
                    setYtdError(`Downloaded — ${employeeCount} employee${employeeCount !== 1 ? 's' : ''}, ${periodsIncluded} period${periodsIncluded !== 1 ? 's' : ''}`);
                  } catch (err) {
                    const msg = err?.message || 'Download failed';
                    setYtdError(msg.includes('not-found') ? `No locked periods found for ${ytdYear}` : msg);
                  } finally {
                    setYtdLoading(false);
                  }
                }}
                disabled={ytdLoading}
                className="px-4 py-2 text-sm font-semibold bg-brand-blue text-white rounded-lg hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {ytdLoading ? 'Generating…' : 'Download CSV'}
              </button>
            </div>
            {ytdError && (
              <p className={`text-xs mt-2 px-2 py-1.5 rounded-lg ${
                ytdError.startsWith('Downloaded')
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-red-50 text-red-600 border border-red-100'
              }`}>
                {ytdError}
              </p>
            )}
          </div>

          {/* Period list */}
          <p className="text-xs text-neutral-400 px-1">Tap any period to load it in the Overview.</p>
          {periods.map((p) => {
            const { label }    = parsePeriod(p.id);
            const isSelected   = p.id === selectedId;
            const locked       = p.status === 'locked' || p.status === 'exported';
            const statusLabel  = p.status === 'exported' ? 'Exported' : p.status === 'locked' ? 'Locked' : p.status === 'open' ? 'Open' : p.status || 'Open';
            const statusColor  = locked
              ? 'bg-neutral-200 text-neutral-500'
              : 'bg-emerald-100 text-emerald-700 border border-emerald-200';

            return (
              <button
                key={p.id}
                onClick={() => { setSelectedId(p.id); setSubTab('overview'); }}
                className={`w-full text-left rounded-xl px-4 py-3.5 border transition-colors ${
                  isSelected
                    ? 'bg-neutral-900 border-neutral-900'
                    : 'bg-white border-neutral-200 hover:border-neutral-300 active:bg-neutral-50'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold truncate ${isSelected ? 'text-white' : 'text-neutral-800'}`}>
                      {label}
                    </p>
                    {p.reviewedAt && (
                      <p className={`text-xs mt-0.5 ${isSelected ? 'text-neutral-400' : 'text-neutral-400'}`}>
                        Reviewed
                        {p.lockedAt?.toDate
                          ? ` · Locked ${p.lockedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                          : ''}
                      </p>
                    )}
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${isSelected ? 'bg-white text-neutral-900' : statusColor}`}>
                    {locked ? `🔒 ${statusLabel}` : statusLabel}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ═══════════════════════ SETTINGS ═══════════════════════════════ */}
      {subTab === 'settings' && (
        <PayrollTab restaurantId={restaurantId} />
      )}

      {/* ── Employee drilldown sheet ─────────────────────────────────── */}
      {drillEmp && (
        <EmployeeDrillSheet
          emp={drillEmp}
          totalLabor={totalLabor}
          openShifts={openShifts}
          todayClosedShifts={todayClosedShifts}
          lateIncidents={lateArrivals.filter(i => i.empId === drillEmp.id)}
          restaurantId={restaurantId}
          onClose={() => setDrillEmp(null)}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EmployeeDrillSheet
// ─────────────────────────────────────────────────────────────────────────────

const EmployeeDrillSheet = ({ emp, totalLabor, openShifts, todayClosedShifts, lateIncidents, restaurantId, onClose }) => {
  useBodyScrollLock();

  // Manual clock-out state
  const [coStep,     setCoStep]     = useState('idle'); // 'idle' | 'confirm' | 'done'
  const [coTime,     setCoTime]     = useState('');     // local datetime-local string
  const [coNote,     setCoNote]     = useState('');
  const [coLoading,  setCoLoading]  = useState(false);
  const [coError,    setCoError]    = useState('');

  const openShift       = openShifts?.find(s => s.serverID === emp.id);
  const clockedOutToday = !openShift && todayClosedShifts?.some(s => s.serverID === emp.id);

  // Default coTime to "now" when entering confirm step
  const enterConfirm = () => {
    const now = new Date();
    // datetime-local value format: "YYYY-MM-DDTHH:mm"
    const pad = (n) => String(n).padStart(2, '0');
    const local = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setCoTime(local);
    setCoNote('');
    setCoError('');
    setCoStep('confirm');
  };

  const handleClockOut = async () => {
    if (!openShift) return;
    setCoLoading(true);
    setCoError('');
    try {
      // Convert local datetime-local string to ISO
      const iso = coTime ? new Date(coTime).toISOString() : new Date().toISOString();
      await manualClockOutFn({
        restaurantId,
        employeeId: emp.id,
        clockOutIso: iso,
        note: coNote.trim() || undefined,
      });
      setCoStep('done');
    } catch (e) {
      const msg = e?.message || 'Clock out failed. Please try again.';
      setCoError(msg.includes('before clock-in') ? 'Time cannot be before their clock-in time.' : msg);
    } finally {
      setCoLoading(false);
    }
  };

  const share     = totalLabor > 0 ? (emp.grossTotal / totalLabor) * 100 : 0;
  const breakdown = [
    { label: 'Base Pay',           amount: emp.baseAmount,  hours: emp.hours,   show: emp.baseAmount > 0  },
    { label: 'Overtime Premium',   amount: emp.otPremium,   hours: emp.otHours, show: emp.otPremium > 0   },
    { label: 'Tips',               amount: emp.tipAmount,                       show: emp.tipAmount > 0   },
    { label: 'Commission',         amount: emp.commAmount,                      show: emp.commAmount > 0  },
    { label: 'Bonus / Adjustment', amount: emp.bonusAmount,                     show: emp.bonusAmount > 0 },
  ].filter((b) => b.show);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50 sm:px-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[90dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

        <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <div>
            <p className="text-base font-semibold text-neutral-800">{emp.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-neutral-400">{emp.position || emp.role}</p>
              {openShift && (
                <span className="flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                  Clocked In
                </span>
              )}
              {clockedOutToday && (
                <span className="text-xs font-semibold px-1.5 py-0.5 bg-neutral-100 text-neutral-500 rounded-full">
                  Clocked Out
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-lg leading-none">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

          <div className="grid grid-cols-3 gap-2.5">
            {[
              { label: 'Hours',      value: fmtHrs(emp.hours)   },
              { label: 'Gross Pay',  value: fmt$(emp.grossTotal) },
              { label: '% of Labor', value: fmtPct(share)        },
            ].map(({ label, value }) => (
              <div key={label} className="bg-neutral-50 rounded-xl p-3 text-center">
                <p className="text-xs text-neutral-400 mb-1 leading-tight">{label}</p>
                <p className="text-sm font-bold text-neutral-800">{value}</p>
              </div>
            ))}
          </div>

          {emp.effectiveRate != null && (
            <div className="bg-neutral-50 rounded-xl px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-neutral-600">Effective hourly rate</p>
              <p className="text-sm font-bold text-neutral-800">{fmt$(emp.effectiveRate)}/hr</p>
            </div>
          )}

          {breakdown.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">Pay Breakdown</p>
              <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100 overflow-hidden">
                {breakdown.map((b) => (
                  <div key={b.label} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-neutral-700">{b.label}</p>
                      {b.hours > 0 && <p className="text-xs text-neutral-400">{fmtHrs(b.hours)}</p>}
                    </div>
                    <p className="text-sm font-semibold text-neutral-800">{fmt$(b.amount)}</p>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 py-3 bg-neutral-50">
                  <p className="text-sm font-bold text-neutral-800">Gross Total</p>
                  <p className="text-sm font-bold text-neutral-800">{fmt$(emp.grossTotal)}</p>
                </div>
              </div>
            </div>
          )}

          {emp.otHours > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-sm font-semibold text-amber-800">Overtime this period</p>
              <p className="text-xs text-amber-700 mt-0.5">{fmtHrs(emp.otHours)} OT · {fmt$(emp.otPremium)} in premium pay</p>
            </div>
          )}

          {emp.hours >= 35 && emp.otHours === 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
              <p className="text-sm font-semibold text-orange-800">Approaching overtime</p>
              <p className="text-xs text-orange-700 mt-0.5">{fmtHrs(emp.hours)} logged · {fmtHrs(40 - emp.hours)} until threshold</p>
            </div>
          )}

          {emp.isTipped && emp.tipAmount === 0 && emp.hours > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <p className="text-sm font-semibold text-red-700">No tips recorded</p>
              <p className="text-xs text-red-600 mt-0.5">This employee is marked as tipped but has $0 in tip income this period.</p>
            </div>
          )}

          {lateIncidents?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">
                Late Arrivals this Period
              </p>
              <div className="bg-white border border-red-200 rounded-xl divide-y divide-red-100 overflow-hidden">
                {lateIncidents.map((inc, i) => {
                  const d = new Date(inc.date + 'T00:00:00');
                  const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  const fmtT = (t) => t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  return (
                    <div key={i} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-neutral-700">{dayLabel}</p>
                        <p className="text-xs text-neutral-400">
                          Scheduled {fmtT(inc.scheduledStart)} · Clocked in {fmtT(inc.actualClockIn)}
                        </p>
                      </div>
                      <span className="text-xs font-bold text-red-600 shrink-0 ml-3">
                        +{inc.lateMin} min
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {openShift && coStep === 'idle' && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-green-800">Currently clocked in</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    Since {openShift.clockInTime?.toDate
                      ? openShift.clockInTime.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : '—'}
                  </p>
                </div>
                <button
                  onClick={enterConfirm}
                  className="shrink-0 px-3 py-1.5 text-xs font-semibold bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors"
                >
                  Clock Out
                </button>
              </div>
            </div>
          )}

          {openShift && coStep === 'confirm' && (
            <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-red-50 border-b border-red-100">
                <p className="text-sm font-semibold text-red-800">Manual clock-out</p>
                <p className="text-xs text-red-700 mt-0.5 leading-relaxed">
                  Set the actual time they stopped working. This will be logged in the audit record and used for payroll hours.
                </p>
              </div>
              <div className="px-4 py-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Clock-out time</label>
                  <input
                    type="datetime-local"
                    value={coTime}
                    onChange={(e) => setCoTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg text-neutral-800 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Note (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Employee forgot to clock out"
                    value={coNote}
                    onChange={(e) => setCoNote(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg text-neutral-800 placeholder-neutral-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                  />
                </div>
                {coError && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{coError}</p>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setCoStep('idle')}
                    disabled={coLoading}
                    className="flex-1 py-2 text-sm font-medium border border-neutral-200 text-neutral-600 rounded-lg hover:bg-neutral-50 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClockOut}
                    disabled={coLoading || !coTime}
                    className="flex-1 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {coLoading ? 'Clocking out…' : 'Confirm Clock Out'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {coStep === 'done' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
              <p className="text-sm font-semibold text-emerald-800">Clocked out</p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Timesheet closed and audit record saved. Payroll hours will update shortly.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayrollView;
